import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Loader2, Check, Power, Trash2, Upload } from "lucide-react";
import {
  superListCampanas,
  superCreateCampana,
  superActivateCampana,
  superDeactivateAllCampanas,
  superDeleteCampana,
  superUploadAsset,
  type CampanaRow,
} from "../../lib/api";
import { Modal } from "../../components/ui/Modal";
import { HelpPopover } from "../../components/admin/forms/HelpPopover";
import { useDialogs } from "../../hooks/useDialogs";
import { useUndoableDelete } from "../../hooks/useUndoableDelete";

export default function Campanas() {
  const dialogs = useDialogs();
  const undoDelete = useUndoableDelete();
  const [campanas, setCampanas] = useState<CampanaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      setCampanas(await superListCampanas());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  async function handleActivate(id: number) {
    setActionLoading(id);
    try {
      await superActivateCampana(id);
      refetch();
    } catch (e) {
      await dialogs.alert({
        message: e instanceof Error ? e.message : String(e),
        variant: "error",
      });
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDeactivateAll() {
    const ok = await dialogs.confirm({
      title: "Desactivar campaña",
      message: "Esto va a desactivar la campaña actual en todas las sucursales. ¿Confirmás?",
      confirmLabel: "Desactivar",
      variant: "destructive",
    });
    if (!ok) return;
    setActionLoading(-1);
    try {
      await superDeactivateAllCampanas();
      refetch();
    } catch (e) {
      await dialogs.alert({
        message: e instanceof Error ? e.message : String(e),
        variant: "error",
      });
    } finally {
      setActionLoading(null);
    }
  }

  function handleDelete(c: CampanaRow) {
    if (c.activa === 1) {
      dialogs.alert({
        title: "No se puede borrar",
        message: "No podés borrar la campaña activa. Desactivá primero.",
        variant: "error",
      });
      return;
    }
    undoDelete({
      key: c.id,
      message: `Campaña "${c.nombre_display}" borrada`,
      removeOptimistic: () =>
        setCampanas((prev) => prev.filter((x) => x.id !== c.id)),
      restore: () =>
        setCampanas((prev) =>
          prev.some((x) => x.id === c.id) ? prev : [...prev, c],
        ),
      commit: () => superDeleteCampana(c.id),
      onError: (e) => {
        dialogs.alert({
          message: e instanceof Error ? e.message : String(e),
          variant: "error",
        });
        refetch();
      },
    });
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-end">
        <h1 className="sr-only">
          Campañas
          <HelpPopover>
            {"La campaña activa se muestra en TODAS las sucursales (en el " +
              "cuerpo de la pantalla). El cambio es instantáneo, sin deploy."}
          </HelpPopover>
        </h1>
        <button
          onClick={() => setCreating(true)}
          data-tour="campanas-new"
          className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] px-4 py-2 font-bold"
        >
          <Plus className="h-4 w-4" />
          Nueva campaña
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-500/15 p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <Loader2 className="h-8 w-8 animate-spin opacity-50" />
      ) : campanas.length === 0 ? (
        <div className="rounded-lg bg-white/5 p-8 text-center opacity-70">
          No hay campañas. Creá la primera.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {campanas.some((c) => c.activa === 1) && (
            <button
              onClick={handleDeactivateAll}
              disabled={actionLoading === -1}
              className="self-start rounded-lg bg-white/10 px-4 py-2 text-sm hover:bg-white/20 disabled:opacity-50"
            >
              {actionLoading === -1 ? (
                <Loader2 className="inline h-4 w-4 animate-spin" />
              ) : (
                <Power className="inline h-4 w-4" />
              )}{" "}
              Desactivar campaña actual
            </button>
          )}

          {/* Grid (pedido): las campañas lado a lado en vez de filas
              full-width apiladas. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {campanas.map((c) => (
            <div
              key={c.id}
              className={`flex items-center gap-4 rounded-lg p-3 ${
                c.activa === 1
                  ? "border-2 border-emerald-500/40 bg-emerald-500/5"
                  : "bg-white/5"
              }`}
            >
              <img
                src={c.imagen_url}
                alt={c.nombre_display}
                className="h-16 w-16 rounded object-cover bg-black/30"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold">{c.nombre_display}</h3>
                  {c.activa === 1 && (
                    <span className="rounded bg-emerald-500 px-2 py-0.5 text-xs text-black">
                      ACTIVA
                    </span>
                  )}
                </div>
                <p className="text-xs opacity-60">{c.nombre_interno}</p>
              </div>
              <div className="flex gap-2">
                {c.activa === 0 && (
                  <button
                    onClick={() => handleActivate(c.id)}
                    disabled={actionLoading === c.id}
                    className="flex items-center gap-1 rounded bg-emerald-600 px-3 py-1.5 text-sm hover:bg-emerald-500 disabled:opacity-50"
                  >
                    {actionLoading === c.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Check className="h-3 w-3" />
                    )}
                    Activar
                  </button>
                )}
                <button
                  onClick={() => handleDelete(c)}
                  disabled={c.activa === 1}
                  className="rounded p-1.5 text-red-400 hover:bg-red-500/15 disabled:opacity-30"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
          </div>
        </div>
      )}

      <Modal open={creating} onClose={() => setCreating(false)} closeOnBackdrop>
        <CampanaForm
          onCancel={() => setCreating(false)}
          onSubmit={async (data) => {
            await superCreateCampana(data);
            setCreating(false);
            refetch();
          }}
        />
      </Modal>
    </div>
  );
}

function CampanaForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (data: {
    nombre_interno: string;
    nombre_display: string;
    imagen_url: string;
  }) => Promise<void>;
  onCancel: () => void;
}) {
  const [nombreInterno, setNombreInterno] = useState("");
  const [nombreDisplay, setNombreDisplay] = useState("");
  const [imagenUrl, setImagenUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function handleUpload(file: File) {
    setError(null);
    setUploading(true);
    try {
      const result = await superUploadAsset(file, "campana");
      setImagenUrl(result.url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!imagenUrl) {
      setError("Subí una imagen primero.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await onSubmit({
        nombre_interno: nombreInterno.trim(),
        nombre_display: nombreDisplay.trim(),
        imagen_url: imagenUrl,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <h2 className="text-2xl font-bold">Nueva campaña</h2>

      <label className="flex flex-col gap-1 text-sm">
        <span className="opacity-80">Nombre interno (snake_case, único)</span>
        <input
          value={nombreInterno}
          onChange={(e) =>
            setNombreInterno(
              e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"),
            )
          }
          placeholder="mundial_2026"
          required
          className="rounded-lg bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#a78bfa]"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="opacity-80">Nombre para mostrar</span>
        <input
          value={nombreDisplay}
          onChange={(e) => setNombreDisplay(e.target.value)}
          placeholder="Mundial 2026"
          required
          className="rounded-lg bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#a78bfa]"
        />
      </label>

      <div>
        <label className="text-sm opacity-80">Imagen de la campaña (PNG/JPG)</label>
        <div className="mt-1 flex items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleUpload(f);
            }}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-sm hover:bg-white/20 disabled:opacity-50"
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            {imagenUrl ? "Cambiar imagen" : "Subir imagen"}
          </button>
          {imagenUrl && (
            <img
              src={imagenUrl}
              alt="preview"
              className="h-12 w-12 rounded object-cover"
            />
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 rounded-lg bg-white/10 py-3 font-bold hover:bg-white/20"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={loading || uploading || !imagenUrl}
          className="flex-1 rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] py-3 font-bold disabled:opacity-50"
        >
          {loading ? "Creando…" : "Crear campaña"}
        </button>
      </div>
    </form>
  );
}
